import LayoutWrapper from "../components/LayoutWrapper";
import HeaderBar from "../components/HeaderBar";
import { useState, useContext, useEffect } from "react";
import getCurrentSkin from "../utils/getCurrentSkin";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { ThemeContext } from "../context/ThemeContext";
import axios from "axios";
import * as XLSX from "xlsx";
import { useLocation, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ItemSubCategoryMas from "./ItemSubCategoryMas";   // ← add this import
import ConfirmModal from "../components/ConfirmModal";


const ItemSubCategory = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const parentCategory = location.state?.parentCategory;

  const [showAddForm, setShowAddForm] = useState(false);
  const [subCategories, setSubCategories] = useState([]);
  const [editSubCategory, setEditSubCategory] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedSubCategories, setSelectedSubCategories] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
const [loading, setLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // Form states
  const [subCategoryName, setSubCategoryName] = useState("");
  const [subCategoryCode, setSubCategoryCode] = useState("");
  const [remarks, setRemarks] = useState("");

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // Session values
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = {
    "x-finyearid": finYearId,
    "x-finyear": finYear,
    "x-branchid": branchId,
    "x-branch": branch,
    "x-userid": userId,
  };

  // Redirect if no parent category
  useEffect(() => {
    if (!parentCategory) {
      navigate("/", { replace: true });
    }
  }, [parentCategory, navigate]);

  const generateNextSubCategoryCode = async () => {
    if (!parentCategory?.fld_itemCategoryCode) return "";

    try {
      const res = await axios.get("https://nissi-india.com/api/itemList_subCatCode", {
        params: { categoryCode: parentCategory.fld_itemCategoryCode },
        headers: commonHeaders,
      });

      const nextCode = res.data?.nextSubCategoryCode || "";
      setSubCategoryCode(nextCode);
      return nextCode;
    } catch (error) {
      console.error("Failed to generate next subcategory code:", error);
      toast.error("Error generating subcategory code.");
      return "";
    }
  };

  const fetchSubCategories = async () => {
    if (!parentCategory?.fld_itemcategoryid) return;

    setLoading(true);
    try {
      const res = await axios.get("https://nissi-india.com/api/itemSubCategoryList", {
        params: {
          categoryId: parentCategory.fld_itemcategoryid,
          search: search.trim(),
          page,
          pageSize: limit,
        },
        headers: commonHeaders,
      });

      setSubCategories(res.data.subCategories || []);
      setTotalCount(res.data.totalCount || 0);
      setTotalPages(Math.ceil((res.data.totalCount || 0) / limit) || 1);
    } catch (error) {
      console.error("Error fetching subcategories:", error);
      setSubCategories([]);
      setTotalCount(0);
      setTotalPages(1);
      toast.error("Failed to fetch subcategories.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubCategories();
  }, [parentCategory, page, search]);

  const openAddForm = async () => {
    setEditSubCategory(null);
    setSubCategoryName("");
    setRemarks("");
    await generateNextSubCategoryCode();
    setShowAddForm(true);
  };

const openEditForm = (sub) => {
    setEditSubCategory(sub);
    setSubCategoryName(sub.fld_itemsubCategoryName || "");

    const rawCode = sub.fld_itemsubCategoryCode || "";
    const cleanCode = rawCode.match(/^\d+/)?.[0] || rawCode;
    setSubCategoryCode(cleanCode);

    setRemarks(sub.fld_remarks || "");
    setShowAddForm(true);
  };

  const handleSave = async () => {
    if (!subCategoryName.trim()) {
      toast.error("Please fill Sub Category Name.");
      return;
    }
    if (!subCategoryCode.trim()) {
      toast.error("Sub Category Code is missing.");
      return;
    }
    if (!parentCategory?.fld_itemcategoryid) {
      toast.error("Parent category id is missing.");
      return;
    }

    try {
      if (editSubCategory) {
        // Update
        const payload = {
          itemsubCategoryCode: subCategoryCode,
          itemsubCategoryName: subCategoryName,
          itemcategoryid: parentCategory.fld_itemcategoryid,
          modifieduserid: Number(userId || 0),
          remarks: remarks || "",
        };

        await axios.put(
          `https://nissi-india.com/api/itemSubCategory_edit/${editSubCategory.fld_itemsubCategoryid}`,
          payload,
          { headers: commonHeaders }
        );
        toast.success("Subcategory updated successfully!");
      } else {
        // Insert
        const payload = {
          itemsubCategoryCode: subCategoryCode,
          itemsubCategoryName: subCategoryName,
          itemcategoryid: parentCategory.fld_itemcategoryid,
          CreatedBy: Number(userId || 0),
          remarks: remarks || "",
        };

        await axios.post("https://nissi-india.com/api/itemSubCategory_insert", payload, {
          headers: commonHeaders,
        });
        toast.success("Subcategory added successfully!");
      }

      setShowAddForm(false);
      setEditSubCategory(null);
      setSubCategoryName("");
      setSubCategoryCode("");
      setRemarks("");
      setSelectedSubCategories([]);
      fetchSubCategories();
    } catch (error) {
      console.error("Save error:", error);
      const serverMsg = error.response?.data?.message || error.response?.data?.error;

      if (error.response?.status === 400 && serverMsg?.toLowerCase().includes("already exists")) {
        toast.error("Item already exists!");
      } else {
        toast.error(serverMsg || "Error saving subcategory. Please try again.");
      }
    }
  };

const handleDeleteSelected = () => {
    if (selectedSubCategories.length === 0) return;
    setShowDeleteConfirm(true);
  };

  const confirmDeleteSelected = async () => {
    setShowDeleteConfirm(false);
    try {
      for (const sub of selectedSubCategories) {
        const id = sub.fld_itemsubCategoryid;
        if (!id) continue;

        await axios.delete(`https://nissi-india.com/api/itemSubCategory_delete/${id}`, {
          headers: commonHeaders,
        });
      }

      setSelectedSubCategories([]);
      fetchSubCategories();
      toast.success("Selected subcategories removed successfully!");
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Error removing subcategories.");
    }
  };

  if (!parentCategory) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-lg text-gray-500">Redirecting...</p>
        </div>
      </div>
    );
  }

  return (
    <LayoutWrapper>
      <HeaderBar
        title={<span className="font-dmserif text-3xl">Item Sub Category</span>}
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div
        className={`font-roboto-slab p-6 rounded-2xl shadow-xl border mt-6 ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : `bg-gray-200 ${currentSkin.glass} text-black`
        }`}
      >
        {/* Top bar */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-4 gap-4">
          <div>
            <h2 className="text-xl font-semibold">
              Parent:{" "}
              <span className="text-blue-600">
                {parentCategory.fld_itemCategoryCode} - {parentCategory.fld_itemcategory_name}
              </span>
            </h2>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch gap-3 w-full sm:w-auto">
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search Sub Categories..."
              className={`px-4 py-2 rounded-lg text-sm placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                isDark
                  ? "bg-gray-800/30 text-gray-200 border-white/20"
                  : "bg-white text-black border-gray-300"
              }`}
            />

            <button
              onClick={openAddForm}
              className={`px-4 py-2 text-white text-sm rounded-lg shadow transition ${buttonClass} ${hoverClass}`}
            >
              + Add New
            </button>
          </div>
        </div>

        {/* Modal Form */}
        {/* Table */}
        {/* Table */}
        <div className="overflow-x-auto">
          <table
            className={`w-full text-sm text-left border border-gray-300 rounded-xl overflow-hidden font-roboto-slab ${
              isDark ? "text-gray-200 bg-white/10" : "text-black bg-white"
            }`}
          >
            <thead className={`text-xs uppercase text-white bg-${baseColor}-${baseShade} shadow-sm`}>
              <tr>
                <th className="px-4 py-3 border-b border-gray-300">S.No</th>
                <th className="px-4 py-3 border-b border-gray-300">Code & Name</th>
                <th className="px-4 py-3 border-b border-gray-300">Remarks</th>
                <th className="px-4 py-3 border-b border-gray-300">Add Inner</th>
                <th className="px-4 py-3 border-b border-gray-300">Edit</th>
                <th className="px-4 py-3 border-b border-gray-300">Select</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-300">
              {loading ? (
                [...Array(limit)].map((_, idx) => (
                  <tr key={idx}>
                    {[...Array(6)].map((_, cell) => (
                      <td key={cell} className="px-4 py-3 border-b border-gray-200">
                        <Skeleton height={20} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : subCategories.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-6 text-gray-500">
                    No sub categories found.
                  </td>
                </tr>
              ) : (
                subCategories.map((sub, idx) => (
                  <tr
                    key={sub.fld_itemsubCategoryid || idx}
                    className={`${!isDark ? "hover:bg-gray-50" : "hover:bg-white/5"} transition`}
                  >
                    <td className="px-4 py-3 border-b border-gray-200">
                      {(page - 1) * limit + idx + 1}
                    </td>
                    <td className="px-4 py-3 border-b border-gray-200 font-medium">
                      {sub.fld_itemsubCategoryCode} ({sub.fld_itemsubCategoryName})
                    </td>
                    <td className="px-4 py-3 border-b border-gray-200">
                      {sub.fld_remarks || "-"}
                    </td>
                    <td className="px-4 py-3 border-b border-gray-200">
                      <button
                        className="text-blue-600 hover:underline"
                        onClick={() =>
                          navigate("/inventory/ItemInnerSubCategoryMaster", {
                            state: {
                              parentCategory: {
                                fld_itemcategoryid: parentCategory.fld_itemcategoryid,
                                fld_itemCategoryCode: parentCategory.fld_itemCategoryCode,
                                fld_itemcategory_name: parentCategory.fld_itemcategory_name,
                                fld_itemsubCategoryid: sub.fld_itemsubCategoryid,
                                fld_itemsubCategoryCode: sub.fld_itemsubCategoryCode,
                                fld_itemsubCategoryName: sub.fld_itemsubCategoryName,
                              },
                            },
                          })
                        }
                      >
                        Add Inner
                      </button>
                    </td>
                    <td className="px-4 py-3 border-b border-gray-200">
                      <button
                        className="text-yellow-500 hover:text-yellow-600"
                        onClick={() => openEditForm(sub)}
                      >
                        ✏️
                      </button>
                    </td>
                    <td className="px-4 py-3 border-b border-gray-200 text-center">
                      <input
                        type="checkbox"
                        checked={selectedSubCategories.some(
                          (item) => item.fld_itemsubCategoryid === sub.fld_itemsubCategoryid
                        )}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedSubCategories((prev) => [...prev, sub]);
                          } else {
                            setSelectedSubCategories((prev) =>
                              prev.filter(
                                (item) => item.fld_itemsubCategoryid !== sub.fld_itemsubCategoryid
                              )
                            );
                          }
                        }}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-400"
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Action buttons */}
          <div className="flex justify-end mt-4 space-x-4">
            <button
              disabled={selectedSubCategories.length === 0}
              onClick={handleDeleteSelected}
              className={`px-4 py-2 text-white text-sm rounded-lg shadow transition ${buttonClass} ${hoverClass} ${
                selectedSubCategories.length === 0 ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              🗑 Remove
            </button>

            <button
              onClick={() => {
                if (subCategories.length === 0) {
                  toast.info("No sub categories available to export.");
                  return;
                }

                const wsData = [
                  ["S.No", "Sub Category Code", "Sub Category Name", "Remarks"],
                  ...subCategories.map((sub, idx) => [
                    idx + 1,
                    sub.fld_itemsubCategoryCode,
                    sub.fld_itemsubCategoryName,
                    sub.fld_remarks || "-",
                  ]),
                ];

                const ws = XLSX.utils.aoa_to_sheet(wsData);
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, "ItemSubCategories");
                XLSX.writeFile(wb, "ItemSubCategories.xlsx");
                toast.success("Exported to Excel!");
              }}
              className="flex items-center gap-2 px-4 py-2 text-white text-sm rounded-lg shadow transition bg-green-600 hover:bg-green-700"
            >
              📥 Export Excel
            </button>
          </div>
        </div>

        {/* Pagination */}
        <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
          <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
            Showing{" "}
            <span className="font-semibold">
              {totalCount === 0 ? 0 : (page - 1) * limit + 1}
            </span>{" "}
            to{" "}
            <span className="font-semibold">{Math.min(page * limit, totalCount)}</span> of{" "}
            <span className="font-semibold">{totalCount}</span> results
          </div>

          {loading ? (
            <div className="flex space-x-2">
              <Skeleton width={80} height={36} borderRadius={12} />
              <Skeleton width={32} height={36} borderRadius={12} />
              <Skeleton width={32} height={36} borderRadius={12} />
              <Skeleton width={80} height={36} borderRadius={12} />
            </div>
          ) : (
            <div className="inline-flex rounded-lg shadow-sm overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 font-sans">
              <button
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                disabled={page === 1}
                className={`px-4 py-2 text-sm font-medium ${
                  page === 1
                    ? "text-gray-400 cursor-not-allowed"
                    : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                }`}
              >
                Prev
              </button>

              {[...Array(totalPages)].map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setPage(idx + 1)}
                  className={`px-4 py-2 text-sm font-medium transition ${
                    page === idx + 1
                      ? `bg-${baseColor}-${baseShade} text-white`
                      : `hover:bg-${baseColor}-100 text-gray-700 dark:hover:bg-${baseColor}-800 dark:text-gray-200`
                  }`}
                >
                  {idx + 1}
                </button>
              ))}

              <button
                onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={page === totalPages}
                className={`px-4 py-2 text-sm font-medium ${
                  page === totalPages
                    ? "text-gray-400 cursor-not-allowed"
                    : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                }`}
              >
                Next
              </button>
            </div>
          )}
        </div>

<ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          closeOnClick
          pauseOnHover
          theme={isDark ? "dark" : "light"}
        />
      </div>

   {showAddForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl">
            <ItemSubCategoryMas
              editData={editSubCategory}
              parentCategory={parentCategory}
              onSuccess={() => {
                toast.success(
                  editSubCategory
                    ? "Subcategory updated successfully!"
                    : "Subcategory added successfully!"
                );
                setShowAddForm(false);
                setEditSubCategory(null);
                fetchSubCategories();
              }}
              onCancel={() => {
                setShowAddForm(false);
                setEditSubCategory(null);
              }}
            />
          </div>
        </div>
      )}

      <ConfirmModal
        open={showDeleteConfirm}
        title="Remove Subcategories"
        message={`Are you sure you want to remove ${selectedSubCategories.length} subcategory(ies)? This action cannot be undone.`}
        confirmLabel="Remove"
        onConfirm={confirmDeleteSelected}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </LayoutWrapper>
  );
};

    
export default ItemSubCategory;